import { useEffect, useRef, useState } from "react";
import { cx } from "../utils/cx";
import { toggleTheme, useTheme } from "../theme/theme";
import styles from "./ProfileMenu.module.css";

interface ProfileMenuProps {
  name: string;
  email: string;
  onSignOut?: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

/**
 * Header profile menu (style-guide.md: Profile Menu). A 28px accent avatar of
 * the user's initials that opens a 280px dropdown with identity and actions.
 */
function ProfileMenu({ name, email, onSignOut }: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.avatar}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${name}`}
        onClick={() => setOpen((v) => !v)}
      >
        {initials(name)}
      </button>

      {open && (
        <div className={styles.panel} role="menu">
          <div className={styles.name}>{name}</div>
          <div className={styles.email}>{email}</div>
          <div className={styles.divider} />
          <button
            type="button"
            role="menuitem"
            className={styles.action}
            onClick={toggleTheme}
          >
            Switch to {theme === "dark" ? "light" : "dark"} theme
          </button>
          {onSignOut && (
            <button
              type="button"
              role="menuitem"
              className={cx(styles.action)}
              onClick={onSignOut}
            >
              Sign out
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ProfileMenu;
